import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitContactForm, validateContactForm } from '../services/contactService';
import Notification from '../components/Notification';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', subject: '', message: '' });
    setFormErrors({});
  };

  const showNotification = (message, type = 'info') => setNotification({ show: true, message, type });
  const hideNotification = () => setNotification({ show: false, message: '', type: 'info' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    const validation = validateContactForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      showNotification('Please correct the errors in the form and try again.', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        resetForm();
        showNotification(result.message, 'success');
      } else {
        if (result.errors) setFormErrors(result.errors);
        showNotification(result.message, 'error');
      }
    } catch {
      showNotification('An unexpected error occurred. Please try again or contact us directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us | YOCILAB"
        description="Get in touch with YOCILAB. Whether you're a young innovator, partner, or funder, we'd love to hear from you."
      />

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.show}
        onClose={hideNotification}
        duration={5000}
      />

      {/* ─── HERO ─── */}
      <section className="contact-hero">
        <div className="contact-hero__bg">
          <div className="contact-hero__overlay" />
        </div>
        <div className="container">
          <div className="contact-hero__badge">
            <span className="contact-hero__badge-dot" />
            GET IN TOUCH
          </div>
          <h1 className="contact-hero__title">
            Let's Build Something<br />
            <em>Meaningful Together</em>
          </h1>
          <p className="contact-hero__subtitle">
            Whether you're a young innovator, potential partner, funder, or simply curious about our work — we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ─── BODY ─── */}
      <section className="contact-body">
        <div className="container">
          <div className="contact-grid">

            {/* Info Panel */}
            <div className="contact-info">
              <div>
                <span className="contact-info__overline">REACH US</span>
                <h2 className="contact-info__title">We're here to help and collaborate.</h2>
                <p className="contact-info__desc">
                  From partnership opportunities to program inquiries, our team is ready to connect. Reach out through any channel below.
                </p>
              </div>

              <div className="contact-info__items">
                <div className="contact-info-item">
                  <div className="contact-info-item__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div>
                    <p className="contact-info-item__label">Location</p>
                    <p className="contact-info-item__value">Yaoundé &amp; Bamenda, Cameroon<br />Central Africa</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-item__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div>
                    <p className="contact-info-item__label">Email</p>
                    <p className="contact-info-item__value">
                      <a href="mailto:info@yocilab.org">info@yocilab.org</a><br />
                      <a href="mailto:partnerships@yocilab.org">partnerships@yocilab.org</a>
                    </p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-item__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="contact-info-item__label">Phone</p>
                    <p className="contact-info-item__value">+237 680 192 095<br />Mon – Fri, 9AM – 6PM WAT</p>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <p className="contact-info-item__label" style={{ marginBottom: '12px' }}>Follow us</p>
                <div className="contact-social">
                  <a
                    href="https://www.facebook.com/yocilab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social__link"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                    Facebook
                  </a>
                  <a
                    href="https://www.linkedin.com/company/yocilab/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social__link"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                    LinkedIn
                  </a>
                  <a
                    href="https://www.instagram.com/yocilab/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social__link"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    Instagram
                  </a>
                  <a
                    href="https://www.tiktok.com/@yocilab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social__link"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298 0 .59.05.86.14V9.42a6.37 6.37 0 0 0-.86-.06A6.34 6.34 0 0 0 3.1 15.7a6.34 6.34 0 0 0 10.82 4.49 6.34 6.34 0 0 0 1.86-4.49V8.58a8.28 8.28 0 0 0 4.81 1.56V6.69z"/>
                    </svg>
                    TikTok
                  </a>
                </div>
              </div>
            </div>

            {/* Form Panel */}
            <div className="contact-form-panel">
              {/* Summit Notice Banner */}
              <div className="contact-summit-banner">
                <div className="contact-summit-banner__badge">YOUTH SUMMIT 2026</div>
                <div className="contact-summit-banner__content">
                  <p className="contact-summit-banner__text">
                    Looking to register for the <strong>YOCILAB Youth Summit</strong> (30th &amp; 31st October 2026 in Bamenda)?
                  </p>
                  <Link to="/youth-summit/register" className="contact-summit-banner__btn">
                    Go to Summit Registration →
                  </Link>
                </div>
              </div>

              <h2 className="contact-form-panel__title">Send us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Full Name <span className="form-required">*</span>
                    </label>
                    <input
                      type="text" id="name" name="name"
                      value={formData.name} onChange={handleChange}
                      className={`form-input${formErrors.name ? ' form-input-error' : ''}`}
                      disabled={isSubmitting} placeholder="Your full name"
                      aria-describedby={formErrors.name ? 'name-error' : undefined}
                    />
                    {formErrors.name && <span id="name-error" className="form-error" role="alert">{formErrors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="form-required">*</span>
                    </label>
                    <input
                      type="email" id="email" name="email"
                      value={formData.email} onChange={handleChange}
                      className={`form-input${formErrors.email ? ' form-input-error' : ''}`}
                      disabled={isSubmitting} placeholder="you@example.com"
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                    />
                    {formErrors.email && <span id="email-error" className="form-error" role="alert">{formErrors.email}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    Subject <span className="form-required">*</span>
                  </label>
                  <select
                    id="subject" name="subject"
                    value={formData.subject} onChange={handleChange}
                    className={`form-input${formErrors.subject ? ' form-input-error' : ''}`}
                    disabled={isSubmitting}
                    aria-describedby={formErrors.subject ? 'subject-error' : undefined}
                  >
                    <option value="">Select a subject</option>
                    <option value="summit">Youth Summit 2026 (Registration / Info)</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="programs">Programs &amp; Services</option>
                    <option value="funding">Funding &amp; Investment</option>
                    <option value="media">Media &amp; Press</option>
                    <option value="careers">Careers &amp; Opportunities</option>
                    <option value="general">General Inquiry</option>
                  </select>
                  {formErrors.subject && <span id="subject-error" className="form-error" role="alert">{formErrors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message <span className="form-required">*</span>
                  </label>
                  <textarea
                    id="message" name="message"
                    value={formData.message} onChange={handleChange}
                    className={`form-textarea${formErrors.message ? ' form-input-error' : ''}`}
                    rows="6" disabled={isSubmitting}
                    placeholder="Please type your message, inquiry, or partnership idea..."
                    aria-describedby={formErrors.message ? 'message-error' : undefined}
                  />
                  {formErrors.message && <span id="message-error" className="form-error" role="alert">{formErrors.message}</span>}
                  <span className="form-hint">{formData.message.length}/1000</span>
                </div>

                <button
                  type="submit"
                  className={`contact-submit-btn${isSubmitting ? ' loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22,2 15,22 11,13 2,9 22,2"/>
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
